import { useMemo, useState } from "react";
import { User, X } from "lucide-react";
import type { Owner } from "@/api/types";

export type OwnerRole = "primary" | "secondary";

interface OwnerFormProps {
  users: Owner[];
  primaryOwner: string | null;
  secondaryOwners: string[];
  onSubmit: (role: OwnerRole, name: string) => void;
  onCancel: () => void;
  submitting?: boolean;
  /** When false, only secondary owners can be added (non-admin primary owners). */
  allowPrimaryRole?: boolean;
}

/** Add-owner flow, mirroring ContactForm: pick a role (primary/secondary), then
 *  search the org's users and click one to assign. */
export default function OwnerForm({
  users,
  primaryOwner,
  secondaryOwners,
  onSubmit,
  onCancel,
  submitting,
  allowPrimaryRole = true,
}: OwnerFormProps) {
  const [role, setRole] = useState<OwnerRole>("secondary");
  const [q, setQ] = useState("");

  // Users eligible for the chosen role. Primary excludes the current primary;
  // secondary excludes the current primary and anyone already secondary.
  const available = useMemo(
    () =>
      users.filter((u) =>
        role === "primary"
          ? u.name !== primaryOwner
          : u.name !== primaryOwner && !secondaryOwners.includes(u.name),
      ),
    [users, role, primaryOwner, secondaryOwners],
  );

  const term = q.trim().toLowerCase();
  const results = term ? available.filter((u) => u.name.toLowerCase().includes(term)) : available;

  return (
    <div className="panel-tight p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="label !mb-0">Add owner</div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          aria-label="Cancel"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {allowPrimaryRole && (
        <div>
          <label className="label">Role</label>
          <select
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as OwnerRole)}
          >
            <option value="primary">Primary owner</option>
            <option value="secondary">Secondary owner</option>
          </select>
        </div>
      )}

      <div>
        <label className="label">Search users</label>
        <input
          className="input"
          placeholder="Type to filter…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Search users"
          autoFocus
        />
      </div>

      {results.length === 0 ? (
        <div className="text-sm text-mut-300 italic">
          {available.length === 0 ? "No users available for this role." : "No matches."}
        </div>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-y-auto">
          {results.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                className="w-full text-left panel-tight p-2 flex items-center gap-2 disabled:opacity-50"
                onClick={() => onSubmit(role, u.name)}
                disabled={submitting}
              >
                <User className="w-3.5 h-3.5 shrink-0 text-mut-300" />
                <span className="text-md truncate">{u.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
